import React from 'react';
import PropTypes from 'prop-types';
import { Flex } from 'rebass';
import { BiInfoCircle, BiMinus, BiPlus } from 'react-icons/bi';
import { CheckboxBox } from '../styled';
import { MdCheck } from 'react-icons/md';
import { formatPrice } from './money';
import {
  OptionRow,
  OptionNameButton,
  OptionNameText,
  OptionRight,
  OptionPrice,
  OptionPriceStruck,
  IncludedLabel,
  IneligibleReason,
  CheckboxButton,
  Stepper,
  StepperButton,
  StepperValue,
} from './styled';

// Groups whose ineligibility means "the journal already has this" — the design
// shows those as a struck price + "Included" rather than the raw server reason.
// An ineligible upgrade (e.g. "already on Platinum") has no such "included"
// reading, so it falls back to showing the reason text itself.
const ONE_TIME_GROUPS = ['add_on', 'feature', 'shipping', 'pages', 'copies'];

// Only add-ons (and upgrades) have a marketing detail view in the design — pages,
// copies and shipping rows are plain, matching the screenshot's Shipping row.
const HAS_DETAIL = group => group === 'add_on' || group === 'feature' || group === 'upgrade';

const rowLabel = option => {
  if (option.group === 'copies' && option.variantTitle) {
    return `${option.title} — ${option.variantTitle}`;
  }
  return option.title;
};

const PurchaseRow = ({ option, quantity, selected, onToggle, onQuantityChange, onShowDetail }) => {
  const disabled = !option.eligible;
  const label = rowLabel(option);
  const showStepper = option.quantitySelectable && option.eligible;

  const name = HAS_DETAIL(option.group) ? (
    <OptionNameButton type="button" onClick={() => onShowDetail(option)}>
      {label}
      <BiInfoCircle size={14} />
    </OptionNameButton>
  ) : (
    <OptionNameText disabled={disabled}>{label}</OptionNameText>
  );

  const price = (
    <OptionPrice>{formatPrice(option.priceCents, option.currency)}</OptionPrice>
  );

  const renderPriceArea = () => {
    if (!disabled) return price;
    if (ONE_TIME_GROUPS.includes(option.group)) {
      return (
        <Flex width="100%" justifyContent='space-between'>
          <OptionPriceStruck>{formatPrice(option.priceCents, option.currency)}</OptionPriceStruck>
          <IncludedLabel>Included</IncludedLabel>
        </Flex>
      );
    }
    return <IneligibleReason>{option.ineligibleReason || 'Not available'}</IneligibleReason>;
  };

  return (
    <OptionRow disabled={disabled} title={disabled ? option.ineligibleReason || undefined : undefined}>
      {name}
      <OptionRight>
        {!disabled && price}
        {disabled && renderPriceArea()}
        {showStepper ? (
          <Stepper>
            <StepperButton
              type="button"
              disabled={quantity <= 0}
              aria-label={`Fewer ${option.title}`}
              onClick={() => onQuantityChange(option.id, Math.max(0, quantity - 1))}
            >
              <BiMinus size={14} />
            </StepperButton>
            <StepperValue>{quantity}</StepperValue>
            <StepperButton
              type="button"
              disabled={quantity >= option.maxQuantity}
              aria-label={`More ${option.title}`}
              onClick={() => onQuantityChange(option.id, Math.min(option.maxQuantity, quantity + 1))}
            >
              <BiPlus size={14} />
            </StepperButton>
          </Stepper>
        ) : (
          !disabled && (
            <CheckboxButton type="button" aria-pressed={selected} onClick={() => onToggle(option.id)}>
              <CheckboxBox checked={selected}>{selected && <MdCheck size={16} />}</CheckboxBox>
            </CheckboxButton>
          )
        )}
      </OptionRight>
    </OptionRow>
  );
};

PurchaseRow.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    group: PropTypes.string,
    eligible: PropTypes.bool,
    ineligibleReason: PropTypes.string,
    priceCents: PropTypes.number,
    currency: PropTypes.string,
    quantitySelectable: PropTypes.bool,
    maxQuantity: PropTypes.number,
    variantTitle: PropTypes.string,
  }).isRequired,
  quantity: PropTypes.number,
  selected: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  onShowDetail: PropTypes.func.isRequired,
};

PurchaseRow.defaultProps = {
  quantity: 0,
  selected: false,
};

export default PurchaseRow;
